"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Loader, ArrowRight, CircleCheck } from "lucide-react";
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit,
    control,
    successMessage
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="8582" className="flex items-center gap-2 text-primary animate-in fade-in slide-in-from-bottom-2 duration-500">
        <CircleCheck className="size-5" />
        <p className="text-sm font-medium">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-2">
      <div className="flex gap-2">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem className="flex-1 space-y-0">
              <FormControl>
                <Input placeholder="Your email address" type="email" className="bg-secondary/10 border-secondary/20 text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-primary/50 focus-visible:border-primary h-10 transition-all duration-300" {...field} />
              </FormControl>
              <FormMessage className="text-xs mt-1 text-destructive" />
            </FormItem>} />
        <Button type="submit" size="icon" disabled={isSubmitting} className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0 size-10 shadow-[0_0_15px_rgba(212,175,55,0.2)] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all duration-300">
          {isSubmitting ? <Loader className="size-4 animate-spin" /> : <ArrowRight className="size-4" />}
        </Button>
      </div>
      {submitError && <p className="text-xs text-destructive mt-1">{submitError}</p>}
      <p className="text-[11px] text-muted-foreground/70 font-sans">
        Receive gentle updates and new stories from our sanctuary.
      </p>
    </Form>;
}
function FooterLinks() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-24 bg-secondary/10 rounded animate-pulse" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="space-y-3">
        <li>
          <Link to="/HomePage" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200">
            Home
          </Link>
        </li>
        <li>
          <Link to="/StoryLibraryPage" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200">
            Story Library
          </Link>
        </li>
        <li>
          <Link to="/ShareYourStoryPage" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200">
            Share Your Story
          </Link>
        </li>
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200 block">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function FooterIntelligence() {
  const currentYear = new Date().getFullYear();
  const businessName = "The Story Haven";
  const missionStatement = "The Story Haven is a 501(c)(3) nonprofit dedicated to helping children and families find healing through guided storytelling, creative arts, and movement.";
  const services = ["Story Library", "Caregiver Corner", "Clinician Resources", "Creative Workshops", "Resilience Programs", "Donate & Support"];
  return <section id="connect-with-the-haven" className="dark bg-background text-foreground pt-16 pb-8 border-t border-primary/10">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
          
          <div className="space-y-6">
            <Link to="/HomePage" className="block w-fit flex items-center gap-4">
              <img src="/img/the_story_haven_logo_v2.png" alt="The Story Haven" className="h-10" />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570824/img/image_tj8wwost.png" alt="Attached Logo" className="h-10" />
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-xs font-sans">
              {missionStatement}
            </p>
          </div>

          
          <div className="space-y-6">
            <h3 className="text-sm font-serif font-bold tracking-widest uppercase text-primary">
              Explore
            </h3>
            <MenuProvider menu_id="46" className="w-full">
              <FooterLinks />
            </MenuProvider>
          </div>

          
          <div className="space-y-6">
            <h3 className="text-sm font-serif font-bold tracking-widest uppercase text-primary">
              Resources
            </h3>
            <ul className="space-y-3">
              {services.map((service, i) => <li key={i} data-index={i}>
                  <Link to="#" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200 block">
                    {service}
                  </Link>
                </li>)}
            </ul>
          </div>

          
          <div className="space-y-6">
            <h3 className="text-sm font-serif font-bold tracking-widest uppercase text-primary">
              Stay Connected
            </h3>
            <p className="text-sm text-muted-foreground font-sans">
              Join our circle of hope to receive gentle updates and new stories delivered to your inbox.
            </p>
            <FormProvider formId="newsletter-footer" sectionName="footer-intelligence" defaultValues={{
            email: ""
          }} submitText="Subscribe" successMessage="Welcome to the Haven." className="w-full">
              <NewsletterForm />
            </FormProvider>
          </div>
        </div>

        <Separator className="bg-primary/10 mb-8" />

        
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-sans text-[11px] text-muted-foreground/60 uppercase tracking-widest">
            © {currentYear} {businessName}. Dedicated to hope and healing.
          </p>
          <p className="font-sans text-[11px] uppercase tracking-widest">
            <Link to="mailto:info@thestoryhaven.org" className="text-[#0071E3] hover:underline transition-all">
              info@thestoryhaven.org
            </Link>
          </p>
          <div className="flex gap-6">
            <Link to="#" className="font-sans text-[11px] text-muted-foreground/60 hover:text-secondary-foreground hover:drop-shadow-[0_0_5px_rgba(230,220,255,0.5)] uppercase tracking-widest transition-all">
              Privacy Policy
            </Link>
            <Link to="#" className="font-sans text-[11px] text-muted-foreground/60 hover:text-secondary-foreground hover:drop-shadow-[0_0_5px_rgba(230,220,255,0.5)] uppercase tracking-widest transition-all">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </section>;
}